import { Component, ErrorInfo, ReactNode } from 'react';

import { captureException } from './capture-exception';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered after a render-time crash. Defaults to nothing. */
  fallback?: ReactNode;
  /** Extra side-effect on catch (e.g. logging). Reporting to O2 already happens automatically. */
  onError?: (error: Error, componentStack: string) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * React error boundary that reports render-time errors to OpenObserve RUM — the O2
 * replacement for Sentry's `<ErrorBoundary>`. Written without JSX so it lives cleanly
 * in this (non-app) lib. Renders `fallback` once a child has thrown.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const componentStack = info?.componentStack ?? '';
    captureException(error, { componentStack });
    this.props.onError?.(error, componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
